import { useEffect, useRef, useState } from "react";

import { useQueryClient } from "@tanstack/react-query";

import type { EditorScope } from "@/api/contracts/spec-editor";
import {
  AppDialog,
  AppDialogActions,
  AppDialogBody,
  AppDialogContentText,
  DialogActionsSpacer,
  DialogPrimaryButton,
  DialogSecondaryButton,
  DialogTertiaryButton,
} from "@/components/ui/app-dialog";
import { ConfirmDialog, ConfirmDialogFailure } from "@/components/ui/confirm-dialog";
import { focusPaperThenFirstInput } from "@/components/ui/dialog-focus";
import { ErrorSnackbar } from "@/components/ui/error-snackbar";
import { LookupSelectField } from "@/components/ui/lookup-select-field";
import { MuiTypography } from "@/components/ui/mui-typography";
import {
  manufacturerUsageQueryOptions,
  useCatalogLookupMenu,
  useDeleteCatalogLookup,
  useLookupEditing,
} from "@/hooks/catalog-admin";
import {
  useRenameSpecManufacturer,
  useUpdateSpecSelectionManufacturer,
} from "@/hooks/spec-editor-per-plan";
import type { SpecLinesLine, SpecLinesSelection } from "@/hooks/specifications";
import {
  manufacturerInUseMessage,
  type LookupOption,
  type ManufacturerUsageCounts,
} from "@/lib/catalog-admin";
import { BffUserFacingError } from "@/lib/errors";

// The per-plan row's manufacturer dialog — the donor's
// `PerPlanManufacturerDialog` (`SpecEditIsland/…/LinePerPlan/
// PerPlanManufacturerDialog.tsx`), standalone: #810 mounts it from the row
// #809 renders. The parity enumeration on #858 lists every fact below.
//
// What it carries as read: the two titles and primary labels, the body line,
// the one required field on the catalog's paged manufacturer picker (create
// and rename inline), an unchanged or empty choice closing with no write, the
// save waiting for the server, and Escape, the X and the overlay closing even
// mid-write (the donor hands `onClose` to its `Dialog` unguarded, and only
// Cancel is held). Its draft outlives a close, as the donor's form does — the
// form lives in the always-mounted dialog and is re-seeded only when the
// current manufacturer changes — so a choice abandoned with Cancel is still
// there on the next open.
//
// Three named parity breaks, owner-ruled (#767 R1 and item 4):
//   - **delete** is an explicit control on each row, behind a usage read: a
//     confirm naming the catalog-wide effect, or the refusal sentence. The
//     donor's delete-by-emptied-rename is not offered here (no
//     `onDeleteOption`), so an emptied rename is refused inline;
//   - **Clear** on `Edit manufacturer` nulls the selection's manufacturer;
//   - **a failed save** raises the error snackbar (the donor only logged it).
//     The dialog still closes, as the donor's does — its `onSubmit` calls
//     `onClose()` whatever the answer (`actions.ts:198-201`); staying open is
//     offered to the owner as a break, not taken. Only its own opening,
//     though: a save walked away from never closes a reopened dialog, which
//     the donor's per-row dialog did on the same row (#864 r4222495377 a,
//     owner-ruled as S6 on #903).
//
// One donor defect is not carried: its field is a `Controller` disabled while
// the picker reads (`CustomSelectWithPagination.tsx:125`), and react-hook-form
// drops a disabled field's value on submit, so a Save pressed then closed the
// dialog with nothing writtenNames and nothing said.

const FORM_ID = "add-manufacturer-dialog-form";
const REQUIRED = "This field is required";
/** The donor's fallback-sentence shape for the one new act (`Failed to add
 *  option…`, `Failed to edit option…`), for a failure the server did not word. */
const DELETE_FAILED = "Failed to delete option. Please try again.";

type Manufacturer = { id: number; name: string };

export interface PerPlanManufacturerDialogProps {
  open: boolean;
  specificationId: number;
  versionId: number;
  /** The caller's view of the version, as every Editor verb takes it. */
  scope: EditorScope;
  line: Pick<SpecLinesLine, "id">;
  selection: Pick<SpecLinesSelection, "id">;
  /** The selection's current manufacturer, as the lines read carries it. */
  manufacturer: Manufacturer | null;
  onClose: () => void;
}

/** The delete a row's control asked for, and what the usage read found. */
interface PendingDelete {
  option: LookupOption;
  /** `null` when the usage read failed. */
  usage: ManufacturerUsageCounts | null;
  failure: string | null;
}

const deleteFailure = (cause: unknown) =>
  cause instanceof BffUserFacingError ? cause.message : DELETE_FAILED;

export function PerPlanManufacturerDialog({
  open,
  specificationId,
  versionId,
  scope,
  line,
  selection,
  manufacturer,
  onClose,
}: PerPlanManufacturerDialogProps) {
  const isEditMode = Boolean(manufacturer?.name);
  const queryClient = useQueryClient();
  const { createOption } = useLookupEditing();
  const create = createOption("manufacturer");
  const rename = useRenameSpecManufacturer({ specificationId, versionId });
  const save = useUpdateSpecSelectionManufacturer();
  const remove = useDeleteCatalogLookup();

  // The donor's form value is the option object, so the trigger is named from
  // first paint and a manufacturer the picker never lists — one retired since
  // the selection took it (#861 D2 a) — stays named.
  const [draft, setDraft] = useState<Manufacturer | null>(manufacturer);
  const [refused, setRefused] = useState(false);
  // Re-seeded only when the current manufacturer changes — the donor's
  // `useEffect(…, [manufacturer])` (`:41-47`) — never on open.
  const [seed, setSeed] = useState(manufacturer);
  if (seed?.id !== manufacturer?.id || seed?.name !== manufacturer?.name) {
    setSeed(manufacturer);
    setDraft(manufacturer);
  }

  /** Names the field's writes answered with, for the id `onChange` hands up. */
  const writtenNames = useRef(new Map<number, string>());
  const rememberName = (option: LookupOption) => {
    writtenNames.current.set(option.id, option.name);
    return option;
  };

  const [pendingDelete, setPendingDelete] = useState<PendingDelete | null>(null);
  /**
   * Counted on every close, so a usage read or a write that answers after the
   * dialog has closed (Escape mid-read, mid-save) knows the opening it would
   * answer is over, and opens no confirm into the next one, nor closes it.
   * Counted **synchronously** in the dialog's own close path — an effect
   * lands a commit late, and a read that
   * answers in between, or a reopen batched with the close, would slip past
   * it — and again in an effect for a close the parent makes by itself.
   */
  const closings = useRef(0);
  const close = () => {
    closings.current += 1;
    onClose();
  };
  useEffect(() => {
    if (!open) closings.current += 1;
  }, [open]);
  const refusal =
    pendingDelete?.usage != null
      ? manufacturerInUseMessage(pendingDelete.option.name, pendingDelete.usage)
      : null;

  const target = { specificationId, versionId, scope, lineId: line.id, selectionId: selection.id };
  const saving = save.isPending;
  const clearing = saving && save.variables?.manufacturerId === null;

  const submit = () => {
    if (saving) return;
    if (draft === null) {
      setRefused(true);
      return;
    }
    if (draft.id === manufacturer?.id) {
      close();
      return;
    }
    write(draft.id);
  };

  const clear = () => {
    if (saving) return;
    write(null);
  };

  /**
   * The write, closing on its answer — success or failure, as the donor's
   * `onSubmit` does — only while the opening it started from lasts: a save
   * walked away from (Escape, the X, the backdrop) never closes a later
   * opening, this row's reopen or another row's dialog under the page's one
   * open key (#864 r4222495377 a, S6 on #903).
   */
  const write = (manufacturerId: number | null) => {
    const opening = closings.current;
    save.mutate(
      { ...target, manufacturerId },
      {
        onSettled: () => {
          if (closings.current === opening) close();
        },
      },
    );
  };

  const askToDelete = async (option: LookupOption, menu: { close: () => void }) => {
    remove.reset();
    const opening = closings.current;
    let next: PendingDelete;
    try {
      const usage = await queryClient.fetchQuery(manufacturerUsageQueryOptions(option.id));
      next = { option, usage, failure: null };
    } catch {
      next = { option, usage: null, failure: DELETE_FAILED };
    }
    if (closings.current !== opening) return;
    // One commit: the picker's modal menu gone and the confirm up.
    menu.close();
    setPendingDelete(next);
  };

  const confirmDelete = () => {
    if (!pendingDelete) return;
    const { option } = pendingDelete;
    remove.mutate(
      { kind: "manufacturer", id: option.id },
      {
        onSuccess: () => {
          // The field would otherwise hold an id the picker no longer lists —
          // the donor clears it the same way (`CustomSelectWithPagination
          // .tsx:132-141`).
          setDraft((current) => (current?.id === option.id ? null : current));
          setPendingDelete(null);
        },
        onError: (cause) =>
          setPendingDelete((current) => current && { ...current, failure: deleteFailure(cause) }),
      },
    );
  };

  return (
    <>
      <AppDialog
        open={open}
        onOpenChange={(next) => {
          if (!next) close();
        }}
        title={isEditMode ? "Edit manufacturer" : "Add manufacturer"}
        size="sm"
        // `maxWidth='sm'` and no `fullWidth` (`:61-71`): MUI's shrink-to-fit
        // paper, not the full 600px the other spec dialogs open at.
        fullWidth={false}
        onOpenAutoFocus={focusPaperThenFirstInput}
        actions={
          <AppDialogActions>
            {isEditMode ? (
              <>
                <DialogTertiaryButton loading={clearing} disabled={saving} onClick={clear}>
                  Clear
                </DialogTertiaryButton>
                <DialogActionsSpacer />
              </>
            ) : null}
            <DialogSecondaryButton disabled={saving} onClick={close}>
              Cancel
            </DialogSecondaryButton>
            <DialogPrimaryButton
              type="submit"
              form={FORM_ID}
              loading={saving && !clearing}
              disabled={saving}
            >
              {isEditMode ? "Save" : "Add manufacturer"}
            </DialogPrimaryButton>
          </AppDialogActions>
        }
      >
        {/* `Grid container spacing={2}` — in MUI 9 a *wrapping row* with a 16px
            gap, each cell the full line (`gridGenerator.js:41-43,155-163`). The
            row, not a column, is the point: a wrapping row's max-content is
            its cells laid end to end, so the shrink-to-fit paper is sized by
            the line **and** the field together, plus the gap, and widens as
            the chosen name does. A column would size it by the line alone. */}
        <form
          id={FORM_ID}
          noValidate
          autoComplete="off"
          className="flex flex-row flex-wrap gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            submit();
          }}
        >
          {/* A plain `Typography variant='body1'` (`:83-85`), not
              `DialogContentText`: the paper's own ink, not the secondary. */}
          <MuiTypography variant="body1" color="primary" className="w-full">
            Select a manufacturer for your Per Plan line item.
          </MuiTypography>
          <ManufacturerPicker
            disabled={saving}
            value={draft}
            error={refused && draft === null ? REQUIRED : undefined}
            onPick={setDraft}
            nameOf={(id) => writtenNames.current.get(id)}
            onCreateOption={async (name) => rememberName(await create(name))}
            onRenameOption={async (id, name) => {
              const renamed = rememberName(await rename(id, name));
              setDraft((current) => (current?.id === renamed.id ? renamed : current));
              return renamed;
            }}
            onRequestDelete={askToDelete}
          />
        </form>

        {/* Inside the manufacturer dialog's content, so Radix stacks it as a
            layer over that dialog rather than as an outside press on it. */}
        <ConfirmDialog
          open={pendingDelete !== null}
          onOpenChange={(next) => {
            if (!next) setPendingDelete(null);
          }}
          title={pendingDelete ? `Delete Manufacturer "${pendingDelete.option.name}"` : ""}
          confirmLabel="Delete"
          onConfirm={confirmDelete}
          isSubmitting={remove.isPending}
          // A used manufacturer cannot go, and a failed read cannot say whether
          // this one may; either way the server would refuse or could not be
          // asked honestly, so the verb is held shut beside Cancel.
          confirmDisabled={refusal !== null || pendingDelete?.usage == null}
        >
          {pendingDelete ? (
            <AppDialogBody>
              {pendingDelete.usage ? (
                <AppDialogContentText>
                  {refusal ??
                    `Are you sure that you want to delete the Manufacturer "${pendingDelete.option.name}"? It will be removed from the manufacturer list everywhere in the catalog.`}
                </AppDialogContentText>
              ) : null}
              <ConfirmDialogFailure error={pendingDelete.failure} />
            </AppDialogBody>
          ) : null}
        </ConfirmDialog>
      </AppDialog>
      {/* Beside the dialog rather than in it, so the 10s snackbar outlives a
          close — `UPDATE_SELECTION_MANUFACTURER`'s string, raised by the
          harness on the mutation's error (#767 R1). */}
      <ErrorSnackbar
        open={save.isError}
        message={save.error?.message ?? ""}
        onClose={() => save.reset()}
      />
    </>
  );
}

interface ManufacturerPickerProps {
  value: Manufacturer | null;
  disabled: boolean;
  error: string | undefined;
  onPick: (next: Manufacturer | null) => void;
  /** A name the field's own writes answered with, for a row not yet listed. */
  nameOf: (id: number) => string | undefined;
  onCreateOption: (name: string) => Promise<LookupOption>;
  onRenameOption: (id: number, name: string) => Promise<LookupOption>;
  onRequestDelete: (option: LookupOption, menu: { close: () => void }) => Promise<void>;
}

/**
 * The field, with the picker's read **inside the dialog's content**, which
 * mounts on open and unmounts on close — where the donor's query lives, in its
 * `Dialog`'s children (react-ui `dialog.tsx:116-186`). So the list is read
 * when the dialog opens, under the picker's own loading state, and read again
 * on the next open; held by the always-mounted dialog component instead, it
 * read when the row mounted and never again. The draft stays outside, so it
 * still outlives a close.
 */
function ManufacturerPicker({ value, onPick, nameOf, ...props }: ManufacturerPickerProps) {
  const menu = useCatalogLookupMenu("manufacturer");
  return (
    // The row's second cell, the full line wide (`Grid item sm={12}`).
    <div className="w-full">
      <LookupSelectField
        label="Manufacturer"
        required
        size="small"
        editable
        value={value?.id ?? null}
        unresolvedLabel={value?.name}
        onChange={(id) => {
          if (id === null) {
            onPick(null);
            return;
          }
          const name = menu.options.find((option) => option.id === id)?.name ?? nameOf(id);
          onPick({ id, name: name ?? "" });
        }}
        {...props}
        {...menu}
      />
    </div>
  );
}
